/**
 * DocumentImportDialog — Universal document importer.
 *
 * Flow:
 *   1) User picks a PDF / Word / Excel / CSV file.
 *   2) Backend extracts rows + source columns (pdfplumber for digital
 *      PDFs, openpyxl/csv/python-docx for the others, with a GPT-5.2
 *      fallback for scanned-but-textual PDFs).
 *   3) AI auto-maps source columns onto the target Atrium schema.
 *      The user can adjust any mapping in a dropdown.
 *   4) User picks one of two commit modes:
 *        • Create a NEW Atrium list with the imported rows.
 *        • Merge rows into the currently-active Atrium list.
 *      Then click "Import".
 *
 * Props:
 *   • open / onClose
 *   • targetType        — 'inventory' | 'permits' | 'subcontractors' …
 *                         The dialog passes target columns by reading
 *                         from `TARGETS[targetType]`.
 *   • onCommit(rows[], { mode, newName })
 *                       — caller decides what to do with the imported
 *                         rows. `mode` is 'new' or 'merge'.
 *   • newNameDefault    — pre-fills the "New list name" input.
 *   • activeListName    — shows the label for the "merge" choice.
 */
import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Upload, Loader2, X, Check, FileText, AlertTriangle, FileSpreadsheet, Camera } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Per-target schema — defines which Atrium columns the importer can
   map onto. Keys must match the field names the parent component
   expects (e.g. InventoryTable rows have a `name` + `quantity_on_hand`
   + `vendor` field). */
export const TARGETS = {
  inventory: {
    label: 'OS&E / Inventory list',
    columns: [
      { key: 'po_number', label: 'PO #' },
      { key: 'markit_reference', label: 'Markit reference' },
      { key: 'name', label: 'Item name' },
      { key: 'quantity_on_hand', label: 'Qty on hand' },
      { key: 'quantity_requested', label: 'Qty requested' },
      { key: 'unit_price', label: 'Unit price' },
      { key: 'shipping_cost', label: 'Shipping cost' },
      { key: 'tax_duty', label: 'Tax / Duty' },
      { key: 'currency', label: 'Currency' },
      { key: 'description', label: 'Description' },
      { key: 'brand', label: 'Brand' },
      { key: 'vendor', label: 'Vendor' },
      { key: 'vendor_reference', label: 'Vendor reference' },
      { key: 'source_market', label: 'Source market' },
      { key: 'lead_time', label: 'Lead time' },
      { key: 'shipping_method', label: 'Shipping method' },
      { key: 'freight_forwarder', label: 'Freight forwarder' },
    ],
  },
  permits: {
    label: 'Property Licenses & Permits',
    columns: [
      { key: 'name', label: 'Name of License' },
      { key: 'valid_from', label: 'Valid from' },
      { key: 'valid_until', label: 'Valid until' },
      { key: 'issuing_entity', label: 'Issuing entity' },
      { key: 'reference_nr', label: 'Reference number' },
    ],
  },
  subcontractors: {
    label: 'Subcontractor Register',
    columns: [
      { key: 'name', label: 'Subcontractor name' },
      { key: 'scope_of_work', label: 'Scope of work' },
    ],
  },
  checklist: {
    label: 'Checklist items',
    columns: [
      { key: 'text', label: 'Item' },
      { key: 'note', label: 'Note' },
    ],
  },
};

export default function DocumentImportDialog({
  open,
  onClose,
  targetType,
  onCommit,
  newNameDefault = '',
  activeListName = '',
}) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const target = TARGETS[targetType] || TARGETS.inventory;
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Stage state
  const [stage, setStage] = useState('pick');  // pick | parsing | mapping | committing | done
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null); // { columns, rows, rows_total, extractor, filename }
  const [mapping, setMapping] = useState({}); // { sourceCol: targetKey | null }
  const [aiBusy, setAiBusy] = useState(false);
  const [commitMode, setCommitMode] = useState('new');  // new | merge
  const [newListName, setNewListName] = useState(newNameDefault || '');
  const [error, setError] = useState('');

  const reset = () => {
    setStage('pick'); setFile(null); setParsed(null); setMapping({});
    setError(''); setNewListName(newNameDefault || ''); setCommitMode('new');
  };

  const handleClose = () => { reset(); onClose?.(); };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setNewListName((cur) => cur || f.name.replace(/\.[^.]+$/, ''));
    parseFile(f);
  };

  const parseFile = async (f) => {
    setStage('parsing'); setError('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('use_ai_fallback', 'true');
      const { data } = await axios.post(`${API}/admin/document-imports/parse`, fd, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      if (!data.rows?.length) {
        setError(`No tabular data found in this ${data.ext}. Try uploading an Excel or CSV instead, or share a digital (non-scanned) PDF.`);
        setStage('pick'); setFile(null);
        return;
      }
      setParsed(data);
      // Kick off auto-mapping
      setAiBusy(true);
      try {
        const { data: m } = await axios.post(
          `${API}/admin/document-imports/map-columns`,
          { source_columns: data.columns, target_columns: target.columns, sample_rows: data.rows.slice(0, 3) },
          { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 60000 },
        );
        setMapping(m.mapping || {});
      } catch {
        // Fallback: try a case-insensitive label match
        const fallback = {};
        const tlower = Object.fromEntries(target.columns.map((c) => [c.label.toLowerCase(), c.key]));
        data.columns.forEach((c) => { fallback[c] = tlower[c.toLowerCase()] || null; });
        setMapping(fallback);
      } finally {
        setAiBusy(false);
      }
      setStage('mapping');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not parse the file.');
      setStage('pick'); setFile(null);
    }
  };

  const updateMapping = (src, key) => {
    setMapping((m) => ({ ...m, [src]: key || null }));
  };

  const mappedCount = Object.values(mapping).filter(Boolean).length;

  const doCommit = async () => {
    if (!parsed) return;
    if (mappedCount === 0) {
      toast.error('Map at least one source column before importing.');
      return;
    }
    if (commitMode === 'new' && !(newListName || '').trim()) {
      toast.error('Give the new list a name first.');
      return;
    }
    // Translate parsed.rows[] from source-column-names to target-keys.
    const rows = parsed.rows.map((src) => {
      const out = {};
      Object.entries(mapping).forEach(([sc, tk]) => {
        if (!tk) return;
        const raw = src[sc];
        if (raw == null) return;
        const val = String(raw).trim();
        if (!val) return;
        out[tk] = val;
      });
      return out;
    }).filter((r) => Object.keys(r).length > 0);

    setStage('committing');
    try {
      await onCommit?.(rows, { mode: commitMode, newName: (newListName || '').trim() });
      setStage('done');
      toast.success(`Imported ${rows.length} row${rows.length === 1 ? '' : 's'} from ${parsed.filename}.`);
      // Auto-close after a moment
      setTimeout(() => handleClose(), 900);
    } catch (e) {
      setStage('mapping');
      toast.error(e?.message || 'Import failed.');
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        data-testid="document-import-dialog"
        className="max-w-3xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Import document into {target.label}</DialogTitle>
        <DialogDescription className="sr-only">
          Upload a PDF, Word, Excel, or CSV file to convert it into a native Atrium document.
        </DialogDescription>
        {/* Header */}
        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)', fontFamily: 'Manrope' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.18)' }}>
              <Upload className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80">Universal Document Import</p>
              <h2 className="text-base font-bold text-white truncate">Import to · {target.label}</h2>
            </div>
          </div>
          <button onClick={handleClose} data-testid="document-import-close" className="p-2 rounded-lg text-white hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stage stepper */}
        <div className="px-6 py-2 flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold" style={{ borderBottom: '1px solid #E2DBC8', backgroundColor: '#F7F2E8', color: '#5C6B6B' }}>
          {[
            { id: 'pick', label: '1 · Upload' },
            { id: 'mapping', label: '2 · Map columns' },
            { id: 'done', label: '3 · Import' },
          ].map((s, i) => {
            const active = stage === s.id || (stage === 'parsing' && s.id === 'pick') || (stage === 'committing' && s.id === 'mapping');
            return (
              <div key={s.id} className={`flex items-center gap-2 ${active ? 'text-blue-700' : ''}`}>
                <span>{s.label}</span>
                {i < 2 && <span style={{ opacity: 0.4 }}>›</span>}
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6" style={{ background: '#FAF8F2' }}>
          {error && (
            <div className="mb-4 p-3 rounded-lg flex items-start gap-2 text-xs" style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5' }}>
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {stage === 'pick' && (
            <>
              <p className="text-sm mb-4" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                Upload a <strong>PDF, Word (.docx), Excel (.xlsx)</strong>, or <strong>CSV</strong> file — or <strong>snap a photo</strong> of a paper document. We'll detect the table, fuzzy-match its columns onto the Atrium schema using AI, and let you review the mapping before importing.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="document-import-picker"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-6 rounded-2xl border-2 border-dashed flex flex-col items-center gap-2 transition hover:bg-blue-50"
                  style={{ borderColor: '#1F4FBE', color: '#1F4FBE', background: 'white', fontFamily: 'Manrope' }}
                >
                  <Upload className="w-7 h-7" />
                  <p className="font-bold text-sm">Upload a file</p>
                  <p className="text-[11px] opacity-70 text-center">PDF · DOCX · XLSX · CSV<br />up to 25 MB</p>
                </button>
                <button
                  type="button"
                  data-testid="document-import-snap"
                  onClick={() => cameraInputRef.current?.click()}
                  className="p-6 rounded-2xl border-2 border-dashed flex flex-col items-center gap-2 transition hover:bg-orange-50"
                  style={{ borderColor: '#EA580C', color: '#EA580C', background: 'white', fontFamily: 'Manrope' }}
                >
                  <Camera className="w-7 h-7" />
                  <p className="font-bold text-sm">Snap a photo</p>
                  <p className="text-[11px] opacity-70 text-center">Paper receipt / permit / invoice<br />Opens your camera on mobile</p>
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.xlsx,.xlsm,.csv"
                className="hidden"
                onChange={onPickFile}
              />
              {/* The `capture="environment"` hint launches the rear camera
                  on mobile devices; on desktop it gracefully falls back
                  to a normal file picker filtered to images. */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={onPickFile}
              />
            </>
          )}

          {stage === 'parsing' && (
            <div className="py-12 flex flex-col items-center gap-3 text-sm" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
              <Loader2 className="w-7 h-7 animate-spin" style={{ color: '#1F4FBE' }} />
              <p>Reading <strong>{file?.name}</strong>…</p>
              <p className="text-[11px]">Files run through pdfplumber / openpyxl / python-docx first. Photos and scanned PDFs use GPT-5.2 vision (OCR + table extraction in one call).</p>
            </div>
          )}

          {(stage === 'mapping' || stage === 'committing') && parsed && (
            <MappingView
              parsed={parsed}
              target={target}
              mapping={mapping}
              updateMapping={updateMapping}
              aiBusy={aiBusy}
              commitMode={commitMode}
              setCommitMode={setCommitMode}
              newListName={newListName}
              setNewListName={setNewListName}
              activeListName={activeListName}
              committing={stage === 'committing'}
            />
          )}

          {stage === 'done' && (
            <div className="py-16 flex flex-col items-center gap-3 text-sm" style={{ color: '#15803D', fontFamily: 'Manrope' }}>
              <Check className="w-10 h-10" />
              <p className="text-base font-bold">Import complete</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {(stage === 'mapping' || stage === 'committing') && (
          <div className="px-6 py-3 flex items-center justify-between gap-2 border-t" style={{ borderColor: '#E2DBC8', backgroundColor: '#F7F2E8' }}>
            <button
              data-testid="document-import-cancel"
              onClick={() => { reset(); }}
              disabled={stage === 'committing'}
              className="px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-50"
              style={{ background: 'white', color: '#5C6B6B', border: '1px solid #D6D0C0', fontFamily: 'Manrope' }}
            >
              Start over
            </button>
            <button
              data-testid="document-import-commit"
              onClick={doCommit}
              disabled={stage === 'committing'}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white disabled:opacity-50 transition hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #15803D 0%, #16A34A 100%)', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(21,128,61,0.35)' }}
            >
              {stage === 'committing' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Import {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MappingView({ parsed, target, mapping, updateMapping, aiBusy, commitMode, setCommitMode, newListName, setNewListName, activeListName, committing }) {
  return (
    <div className="space-y-5" style={{ fontFamily: 'Manrope' }}>
      {/* File summary */}
      <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'white', border: '1px solid #E2DBC8' }}>
        <FileSpreadsheet className="w-7 h-7" style={{ color: '#1F4FBE' }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate" style={{ color: '#1A2E2E' }}>{parsed.filename}</p>
          <p className="text-[11px]" style={{ color: '#5C6B6B' }}>
            {parsed.rows_total} row{parsed.rows_total === 1 ? '' : 's'} · {parsed.columns.length} column{parsed.columns.length === 1 ? '' : 's'} · extractor: {parsed.extractor}
          </p>
        </div>
      </div>

      {/* Commit mode */}
      <div>
        <h3 className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: '#003DA5' }}>How should we import these rows?</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <ModeCard
            id="new"
            active={commitMode === 'new'}
            onClick={() => setCommitMode('new')}
            title="Create new list"
            subtitle="Keeps existing Atrium rows untouched. New list named below."
            testid="document-import-mode-new"
          >
            <input
              data-testid="document-import-new-list-name"
              type="text"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="New list name"
              disabled={commitMode !== 'new'}
              className="mt-2 w-full px-3 py-2 rounded-lg text-sm border outline-none disabled:opacity-50"
              style={{ borderColor: '#D6D0C0', background: 'white', color: '#1A2E2E' }}
            />
          </ModeCard>
          <ModeCard
            id="merge"
            active={commitMode === 'merge'}
            onClick={() => setCommitMode('merge')}
            title="Merge into current list"
            subtitle={activeListName ? `Adds rows to "${activeListName}"` : 'Append to the open Atrium list.'}
            testid="document-import-mode-merge"
            disabled={!activeListName}
          />
        </div>
      </div>

      {/* Column mapping */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#003DA5' }}>Column mapping {aiBusy && <Loader2 className="inline w-3 h-3 animate-spin ml-1" />}</h3>
          <span className="text-[10px]" style={{ color: '#5C6B6B' }}>
            {Object.values(mapping).filter(Boolean).length} of {parsed.columns.length} mapped
          </span>
        </div>
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E2DBC8', background: 'white' }}>
          <div className="grid grid-cols-12 gap-0 px-3 py-2 text-[10px] uppercase tracking-wider font-bold" style={{ background: '#F5F0E1', color: '#003DA5' }}>
            <div className="col-span-5">Source column (from your file)</div>
            <div className="col-span-2 text-center">→</div>
            <div className="col-span-5">Atrium target column</div>
          </div>
          {parsed.columns.map((src) => (
            <div key={src} className="grid grid-cols-12 gap-0 px-3 py-2 items-center" style={{ borderTop: '1px solid #F0EBDB' }}>
              <div className="col-span-5">
                <p className="text-sm font-bold truncate" style={{ color: '#1A2E2E' }}>{src}</p>
                {parsed.rows[0]?.[src] !== undefined && (
                  <p className="text-[11px] truncate" style={{ color: '#5C6B6B' }}>e.g. {parsed.rows[0][src]}</p>
                )}
              </div>
              <div className="col-span-2 text-center text-xl" style={{ color: '#9CA3AF' }}>→</div>
              <div className="col-span-5">
                <select
                  data-testid={`document-import-map-${src.replace(/[^A-Za-z0-9_-]+/g, '-')}`}
                  value={mapping[src] || ''}
                  onChange={(e) => updateMapping(src, e.target.value)}
                  disabled={committing}
                  className="w-full px-2 py-1.5 rounded text-sm border outline-none"
                  style={{ borderColor: mapping[src] ? '#15803D' : '#D6D0C0', color: '#1A2E2E', background: 'white' }}
                >
                  <option value="">— Skip this column —</option>
                  {target.columns.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview first rows */}
      <div>
        <h3 className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: '#003DA5' }}>
          Preview · first {Math.min(parsed.rows.length, 5)} row{parsed.rows.length === 1 ? '' : 's'}
        </h3>
        <div className="rounded-xl overflow-auto" style={{ border: '1px solid #E2DBC8', background: 'white' }}>
          <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#003DA5', color: 'white' }}>
                {parsed.columns.map((c) => (
                  <th key={c} className="px-2 py-1.5 text-left font-bold" style={{ minWidth: 100 }}>
                    {c}
                    {mapping[c] && <span className="ml-1 text-[9px] opacity-80">→ {target.columns.find((t) => t.key === mapping[c])?.label}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsed.rows.slice(0, 5).map((r, i) => (
                <tr key={i} style={{ background: i % 2 ? '#F4F8FD' : 'white' }}>
                  {parsed.columns.map((c) => (
                    <td key={c} className="px-2 py-1" style={{ color: '#1A2E2E' }}>{r[c] || ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ModeCard({ active, onClick, title, subtitle, children, testid, disabled }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="p-3 rounded-xl text-left transition disabled:opacity-50"
      style={{
        background: active ? 'linear-gradient(135deg, #EAF2FF 0%, white 100%)' : 'white',
        border: active ? '2px solid #1F4FBE' : '1px solid #E2DBC8',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <p className="text-sm font-bold" style={{ color: active ? '#003DA5' : '#1A2E2E' }}>{title}</p>
      <p className="text-[11px] mt-1" style={{ color: '#5C6B6B' }}>{subtitle}</p>
      {children}
    </button>
  );
}
